/**
 * useRunVerdictWatcher — polls the review-agent sidecar for per-section
 * audit verdicts across an entire run.
 *
 * Why this exists (G-P2-A path c): the backend has no native
 * ``section.needs_approval`` SSE event. The persona-review verdict
 * is the closest signal we have. Translating verdicts → approvals
 * lets the spine flip to ``approval`` for sections the agent
 * itself flagged (major_revisions / refuse_to_file).
 *
 * SRP: this hook owns "what verdict has each section landed at?".
 * It does NOT decide whether a verdict should pause the run (that's
 * verdictTranslator) and does NOT dispatch reducer events (that's
 * the shell's useEffect on the verdict map).
 *
 * Polling strategy:
 *   - Single fetch of /audit/{session} index per cycle (cheap).
 *   - For any moduleId in the index we haven't seen yet, fetch the
 *     full audit + cache its aggregated_verdict.
 *   - Only fetches scorecards for modules with actionable verdicts
 *     (major / refuse) — minor / pass don't need the cost.
 *   - Default cadence: 30s. The verdict landscape changes slowly
 *     (post-section) and we don't want to thrash the sidecar.
 *
 * Lifecycle: starts on mount when (identity != null && enabled);
 * stops + clears on identity change or unmount.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import type { AuditIndexResponse, AuditRecord, PersonaScorecard, Verdict } from "../../components/review-agent/types";
import { ReviewAgentFetchError, fetchAudit, fetchAuditIndex, fetchReview } from "../../services/reviewAgentClient";
import type { RunIdentity } from "../sseAdapter";

// ---------------------------------------------------------------------------
// State shape
// ---------------------------------------------------------------------------

export interface VerdictRecord {
  /** Aggregated verdict from the audit record. */
  verdict: Verdict;
  /** Per-persona scorecards. May be [] when the audit landed before
   *  the scorecards file was written; the popout still surfaces with
   *  a generic message in that case. */
  scorecards: PersonaScorecard[];
  /** Audit timestamp — used for ordering / staleness debugging. */
  endedAt: string;
}

export interface UseRunVerdictWatcherParams {
  identity: RunIdentity | null;
  /** When false, the hook short-circuits — used to pause the watcher
   *  while the run is in non-mid-run states (idle, post-run reset). */
  enabled: boolean;
  /** Override the default 30s cadence. Tests pass 0 to disable
   *  scheduling and call refresh manually. */
  intervalMs?: number;
}

export interface UseRunVerdictWatcherState {
  /** moduleId → verdict record. New entries are appended; existing
   *  entries are updated when the underlying audit's draft_hash changes
   *  (the section was regenerated). */
  verdicts: Record<string, VerdictRecord>;
  /** True while the FIRST audit-index fetch is in flight. Subsequent
   *  refresh cycles don't toggle this — the spine stays usable. */
  loading: boolean;
  /** Most recent fetch error from the sidecar. Cleared on a
   *  successful refresh. Null when healthy. */
  error: string | null;
}

const DEFAULT_INTERVAL_MS = 30_000;

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useRunVerdictWatcher(params: UseRunVerdictWatcherParams): UseRunVerdictWatcherState {
  const { identity, enabled, intervalMs = DEFAULT_INTERVAL_MS } = params;

  const [verdicts, setVerdicts] = useState<Record<string, VerdictRecord>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Refs let the polling loop read the latest state without triggering
  // a resubscribe on every verdict change.
  const verdictsRef = useRef<Record<string, VerdictRecord>>({});
  useEffect(() => {
    verdictsRef.current = verdicts;
  }, [verdicts]);

  // ------------------------------------------------------------------
  // Refresh: fetches the index + any new/changed audits.
  // ------------------------------------------------------------------
  const refresh = useCallback(async (sessionId: string, signal: AbortSignal): Promise<void> => {
    let index: AuditIndexResponse | undefined;
    try {
      index = await fetchAuditIndex(sessionId, signal);
    } catch (err) {
      if (signal.aborted) return;
      if (err instanceof ReviewAgentFetchError && err.status === 404) {
        // Sidecar hasn't written any audit for this session yet —
        // expected during the first ~seconds of a fresh run. Not an
        // error condition.
        setLoading(false);
        return;
      }
      const msg = err instanceof Error ? err.message : String(err);
      setError(msg);
      setLoading(false);
      return;
    }

    const auditIds: string[] = (index.audits ?? []).filter((id) => {
      // Skip the run-level sentinels — they aren't section verdicts.
      return id !== "_document" && id !== "_finalization" && id !== "_planning";
    });

    // Fetch only audits we haven't seen, OR ones whose section
    // status is "drafted" but verdict was previously fetched (the
    // section may have been regenerated). For Phase 5 we keep this
    // simple: refetch any audit not yet in the verdicts map.
    const toFetch = auditIds.filter((id) => !verdictsRef.current[id]);

    if (toFetch.length === 0) {
      setLoading(false);
      setError(null);
      return;
    }

    // Fetch audits in parallel — they're independent.
    const audits = await Promise.allSettled(toFetch.map((id) => fetchAudit(sessionId, id, signal)));
    if (signal.aborted) return;

    const updates: Record<string, VerdictRecord> = {};
    const scorecardFetches: Promise<void>[] = [];

    for (let i = 0; i < toFetch.length; i++) {
      const moduleId = toFetch[i];
      const result = audits[i];
      if (result.status !== "fulfilled") continue;
      const audit: AuditRecord = result.value;

      updates[moduleId] = {
        verdict: audit.aggregated_verdict,
        scorecards: [],
        endedAt: audit.ended_at,
      };

      // Only fetch scorecards for actionable verdicts — saves a
      // round-trip for the common pass / minor_revisions case.
      if (audit.aggregated_verdict === "major_revisions" || audit.aggregated_verdict === "refuse_to_file") {
        scorecardFetches.push(
          (async () => {
            try {
              const review = await fetchReview(sessionId, moduleId, signal);
              if (signal.aborted) return;
              updates[moduleId] = {
                ...updates[moduleId],
                scorecards: review.scorecards ?? [],
              };
            } catch {
              // Scorecard fetch failure is non-fatal — the verdict
              // is the trigger; scorecards are decoration.
            }
          })(),
        );
      }
    }

    if (scorecardFetches.length > 0) {
      await Promise.allSettled(scorecardFetches);
      if (signal.aborted) return;
    }

    setVerdicts((prev) => ({ ...prev, ...updates }));
    setError(null);
    setLoading(false);
  }, []);

  // ------------------------------------------------------------------
  // Polling lifecycle
  // ------------------------------------------------------------------
  useEffect(() => {
    // Reset on identity / enabled change so a re-run doesn't leak
    // verdicts from the prior session.
    setVerdicts({});
    setLoading(true);
    setError(null);
    verdictsRef.current = {};

    if (!enabled || !identity) {
      setLoading(false);
      return;
    }

    const sessionId = `${identity.trial}__${identity.session}`;
    const abort = new AbortController();
    let timer: ReturnType<typeof setTimeout> | null = null;
    let stopped = false;

    async function tick(): Promise<void> {
      if (stopped) return;
      await refresh(sessionId, abort.signal);
      if (stopped || intervalMs <= 0) return;
      timer = setTimeout(tick, intervalMs);
    }

    tick();

    return () => {
      stopped = true;
      abort.abort();
      if (timer) clearTimeout(timer);
    };
  }, [identity, enabled, intervalMs, refresh]);

  return { verdicts, loading, error };
}
